"use client";

import { c as _c } from "react/compiler-runtime";
import { cva } from "class-variance-authority";
import { buttonVariants } from "fumadocs-ui/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "fumadocs-ui/components/ui/popover";
import { useCopyButton } from "fumadocs-ui/utils/use-copy-button";
import { Check, ChevronDown, Copy, ExternalLinkIcon } from "lucide-react";
import { useMemo, useState } from "react";
import { cn } from "@/lib/cn";
const cache = new Map<string, string>();
export function LLMCopyButton({
  /**
   * A URL to fetch the raw Markdown/MDX content of page
   */
  markdownUrl
}: {
  markdownUrl: string;
}) {
  const [isLoading, setLoading] = useState(false);
  const [checked, onClick] = useCopyButton(async () => {
    const cached = cache.get(markdownUrl);
    if (cached) {
      return await navigator.clipboard.writeText(cached);
    }
    setLoading(true);
    try {
      await navigator.clipboard.write([new ClipboardItem({
        "text/plain": fetch(markdownUrl).then(async res => {
          const content = await res.text();
          cache.set(markdownUrl, content);
          return content;
        })
      })]);
    } finally {
      setLoading(false);
    }
  });
  return <button disabled={isLoading} className={cn(buttonVariants({
    color: "secondary",
    size: "sm",
    className: "gap-2 [&_svg]:size-3.5 [&_svg]:text-fd-muted-foreground"
  }))} onClick={onClick}>
			{checked ? <Check /> : <Copy />}
			Copy Markdown
		</button>;
}
const optionVariants = cva("text-sm p-2 rounded-lg inline-flex items-center gap-2 hover:text-fd-accent-foreground hover:bg-fd-accent [&_svg]:size-4");
export function ViewOptions(t0) {
  const $ = _c(27);
  const {
    markdownUrl,
    githubUrl
  } = t0;
  let t1;
  if ($[0] !== markdownUrl) {
    t1 = typeof window !== "undefined" ? new URL(markdownUrl, window.location.origin) : "loading";
    $[0] = markdownUrl;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const fullMarkdownUrl = t1;
  const q = `Read ${fullMarkdownUrl}, I want to ask questions about it.`;
  let items;
  if ($[2] !== githubUrl || $[3] !== q) {
    items = [];
    if (githubUrl) {
      let t2;
      if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
        t2 = <svg fill="currentColor" role="img" viewBox="0 0 24 24"><title>GitHub</title><path d="M12 .297c-6.63 0-12 5.373-12 12 0 5.303 3.438 9.8 8.205 11.385.6.113.82-.258.82-.577 0-.285-.01-1.04-.015-2.04-3.338.724-4.042-1.61-4.042-1.61C4.422 18.07 3.633 17.7 3.633 17.7c-1.087-.744.084-.729.084-.729 1.205.084 1.838 1.236 1.838 1.236 1.07 1.835 2.809 1.305 3.495.998.108-.776.417-1.305.76-1.605-2.665-.3-5.466-1.332-5.466-5.93 0-1.31.465-2.38 1.235-3.22-.135-.303-.54-1.523.105-3.176 0 0 1.005-.322 3.3 1.23.96-.267 1.98-.399 3-.405 1.02.006 2.04.138 3 .405 2.28-1.552 3.285-1.23 3.285-1.23.645 1.653.24 2.873.12 3.176.765.84 1.23 1.91 1.23 3.22 0 4.61-2.805 5.625-5.475 5.92.42.36.81 1.096.81 2.22 0 1.606-.015 2.896-.015 3.286 0 .315.21.69.825.57C20.565 22.092 24 17.592 24 12.297c0-6.627-5.373-12-12-12" /></svg>;
        $[5] = t2;
      } else {
        t2 = $[5];
      }
      let t3;
      if ($[6] !== githubUrl) {
        t3 = {
          title: "Open in GitHub",
          href: githubUrl,
          icon: t2
        };
        $[6] = githubUrl;
        $[7] = t3;
      } else {
        t3 = $[7];
      }
      items.push(t3);
    }
    let t2;
    if ($[8] !== q) {
      t2 = new URLSearchParams({
        hints: "search",
        q
      });
      $[8] = q;
      $[9] = t2;
    } else {
      t2 = $[9];
    }
    const t3 = `https://chatgpt.com/?${t2}`;
    let t4;
    if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
      t4 = <svg role="img" viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg"><title>OpenAI</title><path d="M22.2819 9.8211a5.9847 5.9847 0 0 0-.5157-4.9108 6.0462 6.0462 0 0 0-6.5098-2.9A6.0651 6.0651 0 0 0 4.9807 4.1818a5.9847 5.9847 0 0 0-3.9977 2.9 6.0462 6.0462 0 0 0 .7427 7.0966 5.98 5.98 0 0 0 .511 4.9107 6.051 6.051 0 0 0 6.5146 2.9001A5.9847 5.9847 0 0 0 13.2599 24a6.0557 6.0557 0 0 0 5.7718-4.2058 5.9894 5.9894 0 0 0 3.9977-2.9001 6.0557 6.0557 0 0 0-.7475-7.0729zm-9.022 12.6081a4.4755 4.4755 0 0 1-2.8764-1.0408l.1419-.0804 4.7783-2.7582a.7948.7948 0 0 0 .3927-.6813v-6.7369l2.02 1.1686a.071.071 0 0 1 .038.052v5.5826a4.504 4.504 0 0 1-4.4945 4.4944zm-9.6607-4.1254a4.4708 4.4708 0 0 1-.5346-3.0137l.142.0852 4.783 2.7582a.7712.7712 0 0 0 .7806 0l5.8428-3.3685v2.3324a.0804.0804 0 0 1-.0332.0615L9.74 19.9502a4.4992 4.4992 0 0 1-6.1408-1.6464zM2.3408 7.8956a4.485 4.485 0 0 1 2.3655-1.9728V11.6a.7664.7664 0 0 0 .3879.6765l5.8144 3.3543-2.0201 1.1685a.0757.0757 0 0 1-.071 0l-4.8303-2.7865A4.504 4.504 0 0 1 2.3408 7.872zm16.5963 3.8558L13.1038 8.364 15.1192 7.2a.0757.0757 0 0 1 .071 0l4.8303 2.7913a4.4944 4.4944 0 0 1-.6765 8.1042v-5.6772a.79.79 0 0 0-.407-.667zm2.0107-3.0231l-.142-.0852-4.7735-2.7818a.7759.7759 0 0 0-.7854 0L9.409 9.2297V6.8974a.0662.0662 0 0 1 .0284-.0615l4.8303-2.7866a4.4992 4.4992 0 0 1 6.6802 4.66zM8.3065 12.863l-2.02-1.1638a.0804.0804 0 0 1-.038-.0567V6.0742a4.4992 4.4992 0 0 1 7.3757-3.4537l-.142.0805L8.704 5.459a.7948.7948 0 0 0-.3927.6813zm1.0976-2.3654l2.602-1.4998 2.6069 1.4998v2.9994l-2.5974 1.4997-2.6067-1.4997Z" /></svg>;
      $[10] = t4;
    } else {
      t4 = $[10];
    }
    let t5;
    if ($[11] !== t3) {
      t5 = {
        title: "Open in ChatGPT",
        href: t3,
        icon: t4
      };
      $[11] = t3;
      $[12] = t5;
    } else {
      t5 = $[12];
    }
    items.push(t5);
    let t6;
    if ($[13] !== q) {
      t6 = new URLSearchParams({
        q
      });
      $[13] = q;
      $[14] = t6;
    } else {
      t6 = $[14];
    }
    const t7 = `https://claude.ai/new?${t6}`;
    let t8;
    if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
      t8 = <svg fill="currentColor" role="img" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><title>Anthropic</title><path d="M17.3041 3.541h-3.6718l6.696 16.918H24Zm-10.6082 0L0 20.459h3.7442l1.3693-3.5527h7.0052l1.3693 3.5528h3.7442L10.5363 3.5409Zm-.3712 10.2232 2.2914-5.9456 2.2914 5.9456Z" /></svg>;
      $[15] = t8;
    } else {
      t8 = $[15];
    }
    let t9;
    if ($[16] !== t7) {
      t9 = {
        title: "Open in Claude",
        href: t7,
        icon: t8
      };
      $[16] = t7;
      $[17] = t9;
    } else {
      t9 = $[17];
    }
    items.push(t9);
    let t10;
    if ($[18] !== q) {
      t10 = process.env.NODE_ENV === "development" ? `http://localhost:3003?${new URLSearchParams({
        q
      })}&hints=search&model=google-ai-studio/gemini-3-flash-preview` : `https://playground.routerbench.com?${new URLSearchParams({
        q
      })}&hints=search&model=google-ai-studio/gemini-3-flash-preview`;
      $[18] = q;
      $[19] = t10;
    } else {
      t10 = $[19];
    }
    let t11;
    if ($[20] === Symbol.for("react.memo_cache_sentinel")) {
      t11 = <span className="text-xs font-bold">RouterBench</span>;
      $[20] = t11;
    } else {
      t11 = $[20];
    }
    let t12;
    if ($[21] !== t10) {
      t12 = {
        title: "Open in RouterBench",
        href: t10,
        icon: t11
      };
      $[21] = t10;
      $[22] = t12;
    } else {
      t12 = $[22];
    }
    items.push(t12);
    $[2] = githubUrl;
    $[3] = q;
    $[4] = items;
  } else {
    items = $[4];
  }
  const items_0 = items;
  let t2;
  if ($[23] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = cn(buttonVariants({
      color: "secondary",
      size: "sm",
      className: "gap-2"
    }));
    $[23] = t2;
  } else {
    t2 = $[23];
  }
  let t3;
  if ($[24] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <PopoverTrigger className={t2}>Open<ChevronDown className="size-3.5 text-fd-muted-foreground" /></PopoverTrigger>;
    $[24] = t3;
  } else {
    t3 = $[24];
  }
  let t4;
  if ($[25] !== items_0) {
    t4 = <Popover>{t3}<PopoverContent className="flex flex-col overflow-auto">{items_0.map(_temp)}</PopoverContent></Popover>;
    $[25] = items_0;
    $[26] = t4;
  } else {
    t4 = $[26];
  }
  return t4;
}
function _temp(item) {
  return <a key={item.href} href={item.href} rel="noreferrer noopener" target="_blank" className={cn(optionVariants())}>{item.icon}{item.title}<ExternalLinkIcon className="text-fd-muted-foreground size-3.5 ms-auto" /></a>;
}